import { FetchResult } from '@apollo/client';
import { useMemo } from 'react';
import { useMembershipActions } from 'src/components/SidePanelTabs/MembershipInformation/use-membership-actions';
import { Address, Conversation } from 'src/graphql';
import { GetStripeSubscriptionDetailsResponse } from 'src/graphql/entities/stripe-subscription-details';
import {
  CreateStripeSubscriptionScheduleMutation,
  ScheduleCreationParams,
} from 'src/graphql/entities/stripe-subscription-schedule';

interface MembershipInfoMenuProps {
  conversation?: Conversation;
  address: Address;
  subscriptionDetails?: GetStripeSubscriptionDetailsResponse;
  addCppToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  addPsmToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  onUnpauseSubscription?: () => void;
  isCppDisabled?: boolean;
}

export const useMembershipInfoMenu = ({
  conversation,
  address,
  subscriptionDetails,
  addCppToSchedule,
  addPsmToSchedule,
  onUnpauseSubscription,
  isCppDisabled,
}: MembershipInfoMenuProps) => {
  const {
    accessPaymentPortal,
    sendMembershipEmail,
    followUp,
    addComplimentaryPeriod,
    addPreSoldMembership,
    customSignupLink,
    copyCalendlyLink,
    copySubscriptionId,
    pauseSubscription,
    unpauseSubscription,
  } = useMembershipActions({
    address,
    conversationId: conversation?.id,
    subscriptionDetails,
    addCppToSchedule,
    addPsmToSchedule,
    onUnpauseSubscription,
    isCppDisabled,
  });

  const menuOptions = useMemo(
    () =>
      [
        accessPaymentPortal,
        sendMembershipEmail,
        followUp,
        addComplimentaryPeriod,
        addPreSoldMembership,
        pauseSubscription,
        unpauseSubscription,
        customSignupLink,
        copyCalendlyLink,
        copySubscriptionId,
      ]
        .filter((action) => ('isAllowed' in action ? action.isAllowed : true))
        .map((action) => ({
          label: action.label,
          icon: action.icon,
          testId:
            'testId' in action && typeof action.testId === 'string'
              ? action.testId
              : undefined,
          onClick: action.handler,
        })),
    [
      accessPaymentPortal,
      addComplimentaryPeriod,
      addPreSoldMembership,
      pauseSubscription,
      unpauseSubscription,
      customSignupLink,
      copyCalendlyLink,
      copySubscriptionId,
      followUp,
      sendMembershipEmail,
    ],
  );

  return {
    menuOptions,
  };
};
